'use client';
import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useInView } from 'react-intersection-observer';
import { useAnalytics } from '..';
import { renderTrackedHost, type TrackedHostProps } from '../../components/trackedHost';
import type { ShoppingFinishedProps, SkuViewedProps } from '../../generated';
import { useConstant } from '../../hooks/useConstant';
import { Shibuya } from '../../Shibuya';
import { useAnalyticsPageView } from '../AnalyticsPageView';
import { getCleanLocationHref } from '../urlUtils';

type DataLayerEvent = {
  event: string;
  [key: string]: unknown;
};
declare global {
  interface Window {
    dataLayer?: DataLayerEvent[];
  }
}

type SkuViewedPayload = Omit<SkuViewedProps, 'additional_attributes'> & {
  additional_attributes?: Record<string, unknown>;
};

type ShoppingFinishedPayload = Omit<
  ShoppingFinishedProps,
  'additional_attributes' | 'finish_type'
> & {
  finish_type: Shibuya.FinishType;
  additional_attributes?: Record<string, unknown>;
};

export interface AnalyticsSkuViewedContextValue {
  skuImpressionId: string;
  shoppingFinishedId: string;
  skuViewedPayload?: SkuViewedPayload;
  /** Starts Segment track immediately; returns the track promise (or undefined if skipped). */
  fireShoppingFinishedEvent: (
    props: AnalyticsShoppingFinishedCallbackProps,
  ) => Promise<unknown> | undefined;
  /** @deprecated Prefer fireShoppingFinishedEvent + await. */
  fireShoppingFinishedEventAsync: (props: AnalyticsShoppingFinishedCallbackProps) => Promise<void>;
}

export interface AnalyticsShoppingFinishedCallbackProps {
  destinationUrl: ShoppingFinishedPayload['destination_url'];
  ctaType?: ShoppingFinishedPayload['cta_type'];
  finishType?: Shibuya.FinishType;
  /** Coerced to number before emit (Nexus Shopping Finished). */
  revenueAmount?: string | number;
  revenueType?: ShoppingFinishedPayload['revenue_type'];
  interstitialClientBotSuspected?: ShoppingFinishedPayload['interstitial_client_bot_suspected'];
  monetizable?: SkuViewedPayload['monetizable'];
  overrideShoppingFinishedId?: ShoppingFinishedPayload['shopping_finish_id'];
  entryPointPageUrl?: SkuViewedPayload['entry_point_page_url'];
  additionalAttributes?: Record<string, unknown>;
}

const AnalyticsSkuViewedContext = createContext<AnalyticsSkuViewedContextValue | null>(null);

export interface AnalyticsSkuViewedProps extends TrackedHostProps {
  source: SkuViewedPayload['sku_source'];
  name: SkuViewedPayload['sku_name'];
  productType: SkuViewedPayload['sku_product_type'];
  level: SkuViewedPayload['sku_level'];
  institutionId?: SkuViewedPayload['institution_id'];
  institutionName?: SkuViewedPayload['institution_name'];
  productId?: SkuViewedPayload['product_id'];
  productName?: SkuViewedPayload['product_name'];
  offerId?: SkuViewedPayload['offer_id'];
  offerName?: SkuViewedPayload['offer_name'];
  monetizable: SkuViewedPayload['monetizable'];
  impressionSection: SkuViewedPayload['impression_section'];
  impressionPosition: SkuViewedPayload['impression_position'];
  dangerouslySetShoppingFinishedId?: string;
  /** A single element to clone (or a render function), or any content when `as` is set. */
  children?: React.ReactNode | ((context: AnalyticsSkuViewedContextValue) => React.ReactNode);
  additionalAttributes?: Record<string, unknown>;
}

export const AnalyticsSkuViewed = forwardRef<HTMLElement, AnalyticsSkuViewedProps>(
  (
    {
      children,
      dangerouslySetShoppingFinishedId,
      as,
      className,
      id,
      style,
      'data-testid': testId,
      ...props
    },
    ref,
  ) => {
    const { analytics, deployableName, deployableVersion, deviceId, gitSha } = useAnalytics();
    const { pageViewId, pageName, pageViewReady } = useAnalyticsPageView();
    const [skuViewedPayload, setSkuViewedPayload] = useState<SkuViewedPayload>();
    const skuImpressionId = useConstant(() => crypto.randomUUID());
    const shoppingFinishedId = useConstant(() => {
      return dangerouslySetShoppingFinishedId || crypto.randomUUID().replace(/-/g, '');
    }, ['dangerouslySetShoppingFinishedId']);
    const { ref: inViewRef, inView } = useInView({
      root: null,
      rootMargin: '1px 0px 0px 0px',
      threshold: 0,
      triggerOnce: true,
    });

    const setRefs = useCallback(
      (node: HTMLElement | null) => {
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref && 'current' in ref) {
          ref.current = node;
        }
        inViewRef(node);
      },
      [inViewRef],
    );

    useEffect(() => {
      if (pageViewReady && deviceId && inView) {
        const skuId =
          props.level === 'institution'
            ? props.institutionId
            : props.level === 'product'
              ? props.productId
              : (props.offerId ?? props.productId ?? props.institutionId);
        // Opinionate requires sku_id — skip rather than emit an invalid payload.
        if (!skuId) return;

        const eventPayload: SkuViewedPayload = {
          sku_id: skuId,
          sku_source: props.source,
          sku_name: props.name,
          sku_product_type: props.productType,
          sku_level: props.level,
          institution_id: props.institutionId,
          institution_name: props.institutionName,
          product_id: props.productId,
          product_name: props.productName,
          offer_id: props.offerId,
          offer_name: props.offerName,
          monetizable: props.monetizable,
          impression_page_name: pageName || window.location.pathname,
          impression_page_url: getCleanLocationHref(),
          impression_pageview_id: pageViewId,
          impression_section: props.impressionSection,
          impression_position: props.impressionPosition,
          sku_impression_id: skuImpressionId,
          deployable_name: deployableName,
          deployable_version: deployableVersion,
          traditional_device_id: deviceId,
          ...(props.additionalAttributes && {
            additional_attributes: { ...props.additionalAttributes },
          }),
        };
        analytics?.track(Shibuya.EventName.SKU_VIEWED, eventPayload, {
          context: {
            app: {
              name: deployableName,
              version: gitSha,
            },
          },
        });
        window.dataLayer?.push({
          event: 'SkuViewed',
          ...eventPayload,
        });
        setSkuViewedPayload(eventPayload);
      }
    }, [pageViewReady, deviceId, inView]);

    const buildShoppingFinishedPayload = useCallback(
      ({
        destinationUrl,
        ctaType,
        finishType = 'external_application',
        monetizable = skuViewedPayload?.monetizable,
        interstitialClientBotSuspected,
        overrideShoppingFinishedId,
        revenueAmount,
        revenueType,
        entryPointPageUrl,
        additionalAttributes = {},
      }: AnalyticsShoppingFinishedCallbackProps): ShoppingFinishedPayload | null => {
        // Opinionate requires sku_id + monetizable, and Shopping Finished must follow a
        // SKU Viewed. Surface the skip on dataLayer so dropped clickouts are debuggable
        // instead of silently missing from revenue reporting.
        const skipReason = !inView
          ? 'not_in_view'
          : !skuViewedPayload?.sku_id
            ? 'missing_sku_id'
            : !monetizable
              ? 'missing_monetizable'
              : undefined;
        if (skipReason || !skuViewedPayload?.sku_id || !monetizable) {
          window.dataLayer?.push({
            event: 'ShoppingFinishedSkipped',
            reason: skipReason,
            destination_url: destinationUrl,
            sku_name: props.name,
            impression_section: props.impressionSection,
          });
          return null;
        }

        const revenue =
          revenueAmount == null || revenueAmount === '' ? undefined : Number(revenueAmount);

        return {
          ...skuViewedPayload,
          sku_id: skuViewedPayload.sku_id,
          monetizable,
          destination_url: destinationUrl,
          cta_type: ctaType?.toLowerCase(),
          finish_type: finishType,
          interstitial_client_bot_suspected: interstitialClientBotSuspected,
          ...(revenue != null && !Number.isNaN(revenue) ? { revenue_amount: revenue } : {}),
          revenue_type: revenueType,
          shopping_finish_id: overrideShoppingFinishedId ?? shoppingFinishedId,
          entry_point_page_url: entryPointPageUrl,
          additional_attributes: {
            ...additionalAttributes,
            page_type:
              (skuViewedPayload.additional_attributes?.page_type as string | undefined) ??
              'roundup',
          },
        };
      },
      [inView, skuViewedPayload, shoppingFinishedId, props.name, props.impressionSection],
    );

    const emitShoppingFinished = useCallback(
      (props: AnalyticsShoppingFinishedCallbackProps) => {
        const eventPayload = buildShoppingFinishedPayload(props);
        if (!eventPayload) return undefined;

        const trackPromise = analytics?.track(Shibuya.EventName.SHOPPING_FINISHED, eventPayload, {
          context: {
            app: {
              name: deployableName,
              version: gitSha,
            },
          },
        });
        window.dataLayer?.push({
          event: 'ShoppingFinished',
          ...eventPayload,
        });
        return trackPromise;
      },
      [buildShoppingFinishedPayload, analytics, deployableName, gitSha],
    );

    const fireShoppingFinishedEvent = emitShoppingFinished;
    const fireShoppingFinishedEventAsync = useCallback(
      async (props: AnalyticsShoppingFinishedCallbackProps) => {
        await emitShoppingFinished(props);
      },
      [emitShoppingFinished],
    );
    const context: AnalyticsSkuViewedContextValue = {
      skuImpressionId,
      shoppingFinishedId,
      skuViewedPayload,
      fireShoppingFinishedEvent,
      fireShoppingFinishedEventAsync,
    };
    const child = typeof children === 'function' ? children(context) : children;
    if (!child) {
      throw new Error('Missing Child');
    }
    return (
      <AnalyticsSkuViewedContext.Provider value={context}>
        {renderTrackedHost(
          { as, className, id, style, 'data-testid': testId },
          child,
          setRefs,
          'AnalyticsSkuViewed',
        )}
      </AnalyticsSkuViewedContext.Provider>
    );
  },
);

export const useAnalyticsSkuViewedContext = () => {
  const context = useContext(AnalyticsSkuViewedContext);
  if (!context) {
    throw new Error('useAnalyticsSkuViewedContext must be used within AnalyticsSkuViewed');
  }
  return context;
};
